import React, { useState, useEffect, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Link } from '@material-ui/core';
import moment from 'moment';

import { convertObjToArr, sortArrByKey } from '../../../utils';
import MsgCollapse from '../../../components/MsgCollapse';
import Actions from '../../../store/ducks/appointments';
import { useAppointments } from '../context';
import AppointmentItem from './AppointmentItem';
import useStyles from './styles';
import { AppointmentProvider } from './context';

import AppointmentItemLoading from './AppointmentItem/Loading';

const HistoryAppointments = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { infos } = useAppointments();
  const {
    history: { list: appointmentsObj, allLoaded: listLoaded },
  } = useSelector(state => state.appointments);
  const [appointmentsRequested, setAppointmentsRequested] = useState(false);
  const [nextAppointments, setNextAppointments] = useState([]);
  const [historyAppointments, setHistoryAppointments] = useState([]);

  useEffect(() => {
    if (!appointmentsRequested && !listLoaded) {
      dispatch(Actions.requestAppointments());
      setAppointmentsRequested(true);
    }
  }, [appointmentsRequested, dispatch, listLoaded]);

  useEffect(() => {
    if (typeof appointmentsObj === 'object') {
      const list = convertObjToArr(appointmentsObj).map(item => {
        const [hour, minutes] = item.startTime.split('h');
        const normalizedDate = moment(item.date)
          .hour(Number(hour))
          .minutes(Number(minutes))
          .format();
        return { ...item, date: normalizedDate };
      });
      const sortedList = sortArrByKey(list, 'date', true);
      const now = moment();
      const nextList = sortedList.filter(item =>
        moment(item.date).isSameOrAfter(now),
      );
      const historyList = sortedList.filter(item =>
        moment(item.date).isBefore(now),
      );
      setNextAppointments(nextList);
      setHistoryAppointments(historyList);
    }
  }, [appointmentsObj]);

  const appointmentsPeriod = useMemo(() => {
    const period = infos?.limitConsultant?.periodType;
    switch (period) {
      case 'day':
        return 'diário';
      case 'week':
        return 'semanal';
      case 'month':
        return 'mensal';
      case 'only once':
        return 'único';
      default:
        return '';
    }
  }, [infos]);

  return (
    <AppointmentProvider>
      <Box className={classes.container}>
        <MsgCollapse
          type="alert"
          title="Fique atento!"
          loading={!infos.loaded}
          message={
            <>
              {`Tenha cuidado para não perder seus horários. É possível cancelar
              um agendamento com no mínimo 48h de antecedência, caso contrário,
              poderá apenas informar sua ausência e o agendamento irá descontar
              parte do seu limite ${appointmentsPeriod} de agendamentos.`}
              {infos.term && (
                <>
                  {' Para mais informações, leia os '}
                  <Link
                    href={infos.term}
                    target="_blank"
                    underline="always"
                    color="inherit"
                  >
                    Termos e Condições de uso.
                  </Link>
                </>
              )}
            </>
          }
        />
        {!listLoaded && (
          <>
            <Typography className="appointments-list-title" variant="overline">
              Carregando agendamentos...
            </Typography>
            <Box className="appointments-list-container">
              <AppointmentItemLoading />
            </Box>
          </>
        )}
        {listLoaded && !nextAppointments.length && !historyAppointments.length && (
          <>
            <Typography className="appointments-list-title" variant="overline">
              Histórico de agendamentos:
            </Typography>
            <Box className="appointments-list-item-empty">
              <Typography variant="body2">
                Você ainda não realizou nenhum agendamento.
              </Typography>
            </Box>
          </>
        )}
        {!!nextAppointments.length && (
          <>
            <Typography className="appointments-list-title" variant="overline">
              Próximos agendamentos:
            </Typography>
            <Box className="appointments-list-container">
              {nextAppointments.map(appointment => (
                <AppointmentItem key={appointment.id} {...appointment} />
              ))}
            </Box>
          </>
        )}
        {!!historyAppointments.length && (
          <>
            <Typography className="appointments-list-title" variant="overline">
              Histórico de agendamentos:
            </Typography>
            <Box className="appointments-list-container">
              {historyAppointments.map(appointment => (
                <AppointmentItem key={appointment.id} {...appointment} />
              ))}
            </Box>
          </>
        )}
      </Box>
    </AppointmentProvider>
  );
};

export default memo(HistoryAppointments);
